import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { viaBadge, viaChipStyle } from '../lib/createdVia.js';
import { useProfile } from '../lib/profile.jsx';
import PageHeader from '../components/PageHeader.jsx';
import AlertEditor from '../components/AlertEditor.jsx';
import LivePulsePanel from '../components/LivePulsePanel.jsx';
import SubTabs from '../components/SubTabs.jsx';

// The Alerts surface (insight → action). Grouped by event, like Goals: each event
// lists its metric watchers, with a one-tap "New alert". An alert is a saved rule
// over a dashboard tile's live number — Pulse evaluates it in the background and
// pings the team the moment it crosses. Dual-surface: the same page serves a client
// self-serving and a Howler admin (scoped to the active client by the profile).
// Deep link: ?new=<suiteId> opens the editor for that event.
export default function AlertsPage() {
  const { activeEntityId } = useProfile();
  const [suites, setSuites] = useState([]);
  const [bySuite, setBySuite] = useState({}); // suiteId -> { alerts, canManage, smsAvailable }
  const [editor, setEditor] = useState(null);  // { suiteId, alert, template } | null
  const [suitesLoading, setSuitesLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const handled = useRef(false);
  const [tab, setTab] = useState(params.get('tab') === 'live' ? 'live' : 'alerts'); // 'alerts' | 'live' | 'templates' (?tab=live deep-links from the Owl)
  const [templates, setTemplates] = useState(null);  // reusable templates (this client + global)

  useEffect(() => { api.mySuites().then(setSuites).catch(() => {}).finally(() => setSuitesLoading(false)); }, []);
  const visibleSuites = activeEntityId ? suites.filter((s) => s.entityId === activeEntityId) : suites;

  const loadTemplates = useCallback(() => {
    if (!activeEntityId) { setTemplates([]); return; }
    api.alertTemplates(activeEntityId).then((r) => setTemplates(r.templates || [])).catch(() => setTemplates([]));
  }, [activeEntityId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const loadSuite = useCallback((sid) => {
    api.suiteAlerts(sid).then((r) => setBySuite((m) => ({ ...m, [sid]: r }))).catch(() => {});
  }, []);
  useEffect(() => { visibleSuites.forEach((s) => loadSuite(s.id)); }, [visibleSuites.map((s) => s.id).join(','), loadSuite]); // eslint-disable-line react-hooks/exhaustive-deps
  const reloadAll = () => visibleSuites.forEach((s) => loadSuite(s.id));

  const rows = visibleSuites.map((s) => ({ suite: s, alerts: [], canManage: false, smsAvailable: false, loaded: bySuite[s.id] !== undefined, ...(bySuite[s.id] || {}) }));

  // Deep link from a teaser/onboarding (?new=<suiteId>) or an Actions-hub
  // "Make it happen" (?name=<draft alert name>, suite optional): opens the
  // editor, name pre-filled — the human picks the metric and threshold.
  useEffect(() => {
    if (handled.current || !rows.length) return;
    const newSuite = params.get('new');
    const prefillName = params.get('name') || '';
    if (newSuite || prefillName) {
      const target = rows.find((r) => r.suite.id === newSuite && r.canManage) || rows.find((r) => r.canManage);
      if (target) { setEditor({ suiteId: target.suite.id, alert: null, prefillName }); handled.current = true; const next = new URLSearchParams(params); next.delete('new'); next.delete('name'); setParams(next, { replace: true }); }
    }
  }, [rows, params, setParams]);

  const toggleStatus = (alert) => {
    api.setAlertStatus(alert.id, alert.status === 'paused' ? 'active' : 'paused')
      .then(() => loadSuite(alert.suiteId)).catch((e) => window.alert(e.message));
  };

  // Start a new alert from a saved template — attach it to a manageable event.
  const useTemplate = (t) => {
    const sid = (rows.find((r) => r.canManage)?.suite.id) || visibleSuites[0]?.id;
    if (!sid) { window.alert('Add an event first, then create an alert from a template.'); return; }
    setTab('alerts');
    setEditor({ suiteId: sid, alert: null, template: t.payload });
  };
  const deleteTpl = (t) => {
    if (!window.confirm(`Delete template “${t.payload?.name || t.name}”?`)) return;
    api.deleteAlertTemplate(t.id).then(() => setTemplates((x) => (x || []).filter((y) => y.id !== t.id))).catch((e) => window.alert(e.message));
  };

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '4px 2px 40px' }}>
      <PageHeader kicker="Action" title="Alerts" />
      <p style={{ color: 'var(--muted)', fontSize: 13.5, margin: '0 0 18px', lineHeight: 1.5 }}>
        Get a tap on the shoulder the moment a number matters — a sell-out, a revenue milestone, stock running low.
        Pulse watches the metric for you (checked every few minutes) and tells you on your phone, by email or SMS.
      </p>

      {/* Tabs: live alerts vs the reusable templates available to this client. */}
      <SubTabs
        activeId={tab}
        onSelect={setTab}
        style={{ marginBottom: 18, padding: 0 }}
        tabs={[
          { id: 'alerts', title: 'Alerts', icon: 'alerts' },
          { id: 'live', title: 'Live updates', icon: 'live' },
          { id: 'templates', title: `Templates${templates && templates.length ? ` (${templates.length})` : ''}`, icon: 'notes' },
        ]}
      />

      {/* Live updates — recurring event-day multi-metric snapshots (Live Pulse). */}
      {tab === 'live' && <LivePulsePanel suites={visibleSuites} />}

      {tab === 'templates' && (
        <TemplatesView templates={templates} canUse={rows.some((r) => r.canManage) || visibleSuites.length > 0} onUse={useTemplate} onDelete={deleteTpl} />
      )}

      {tab === 'alerts' && (<>
      {suitesLoading && <Skel w="100%" h={90} r={14} />}

      {!suitesLoading && !rows.length && (
        <div style={empty}>No events yet. Once you have an event, set its first alert here.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        {rows.map(({ suite, alerts = [], canManage, smsAvailable, loaded }) => (
          <section key={suite.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <h2 style={eventName}>{suite.name}</h2>
              <span style={{ flex: 1 }} />
              {loaded && canManage && (
                <button onClick={() => setEditor({ suiteId: suite.id, alert: null })} style={addBtn}>＋ {alerts.length ? 'Add an alert' : 'New alert'}</button>
              )}
            </div>
            {!loaded && <Skel w="100%" h={68} r={12} />}
            {loaded && (alerts.length ? (() => {
              const renderRow = (a) => (
                <AlertRow key={a.id} alert={a} canManage={canManage} onEdit={() => setEditor({ suiteId: suite.id, alert: a })} onToggle={() => toggleStatus(a)} />
              );
              const tagged = alerts.some((a) => (a.tag || '').trim());
              if (!tagged) return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{alerts.map(renderRow)}</div>;
              // One section per tag (insertion order); untagged alerts under "Other".
              const groups = [];
              alerts.forEach((a) => {
                const key = (a.tag || '').trim();
                let grp = groups.find((x) => x.key === key);
                if (!grp) { grp = { key, alerts: [] }; groups.push(grp); }
                grp.alerts.push(a);
              });
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {groups.map((grp) => (
                    <div key={grp.key || '_other'}>
                      <div style={tagHead}>{grp.key || 'Other'}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{grp.alerts.map(renderRow)}</div>
                    </div>
                  ))}
                </div>
              );
            })() : (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>No alerts yet — set one and we’ll watch the number for you.</div>
            ))}
          </section>
        ))}
      </div>
      </>)}

      {editor && (
        <AlertEditor
          entityId={suites.find((s) => s.id === editor.suiteId)?.entityId || activeEntityId}
          suiteId={editor.suiteId}
          suiteName={suites.find((s) => s.id === editor.suiteId)?.name}
          suiteEvent={bySuite[editor.suiteId]?.eventName || ''}
          alert={editor.alert}
          prefillName={editor.prefillName || ''}
          initialTemplate={editor.template || null}
          smsAvailable={!!bySuite[editor.suiteId]?.smsAvailable}
          slackAvailable={!!bySuite[editor.suiteId]?.slackAvailable}
          onClose={() => setEditor(null)}
          onSaved={() => { reloadAll(); loadTemplates(); }}
        />
      )}
    </div>
  );
}

// One alert as a row: name, the rule in a sentence, a live status chip, and the
// channels + last fire. Tap to edit; the ⏸/▶ pauses without losing the rule.
function AlertRow({ alert, canManage, onEdit, onToggle }) {
  const chip = alert.status === 'paused' ? { label: 'Paused', bg: 'rgba(128,128,128,0.15)', fg: 'var(--muted)' }
    : alert.state === 'triggered' ? { label: 'Triggered', bg: 'rgba(220,38,38,0.12)', fg: 'var(--error, #dc2626)' }
      : { label: 'Watching', bg: 'rgba(16,185,129,0.12)', fg: '#059669' };
  const chIcon = { push: '📱', email: '✉️', sms: '💬' };
  return (
    <div style={card}>
      <div role="button" tabIndex={0} onClick={canManage ? onEdit : undefined} onKeyDown={(e) => { if (canManage && (e.key === 'Enter' || e.key === ' ')) onEdit(); }} style={{ flex: 1, minWidth: 0, cursor: canManage ? 'pointer' : 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alert.name}</span>
          <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 980, background: chip.bg, color: chip.fg }}>{chip.label}</span>
          {viaBadge(alert.createdVia) && <span title="Where this alert was created" style={viaChipStyle}>{viaBadge(alert.createdVia).icon} via {viaBadge(alert.createdVia).label}</span>}
          {alert.priority === 'important' && <span title="Important — ignores quiet hours" style={{ fontSize: 11 }}>🔴</span>}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3, lineHeight: 1.4 }}>{ruleText(alert)}</div>
        {/* Filed under one event, watching another. Louder than the measure
            caution because the SECTION HEADING is what a reader trusts to say
            which event a card is about, and here it is wrong. */}
        {alert.eventMismatch && (
          <div style={{ fontSize: 11.5, color: '#b91c1c', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.32)', borderRadius: 8, padding: '5px 9px', marginTop: 6, lineHeight: 1.45, fontWeight: 600 }}>
            ⚠ {alert.eventMismatch}
          </div>
        )}
        {alert.measureCaution && (
          <div style={{ fontSize: 11.5, color: '#b45309', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.28)', borderRadius: 8, padding: '5px 9px', marginTop: 6, lineHeight: 1.45 }}>
            ⚠ {alert.measureCaution}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: 11.5, color: 'var(--muted)', flexWrap: 'wrap' }}>
          <span>🗂 inbox{(alert.channels || []).map((c) => ` · ${chIcon[c] || ''} ${c}`).join('')}</span>
          {alert.lastValue != null && <span>· now {fmtNum(alert.lastValue, alert.unit)}</span>}
          {alert.fireCount > 0 && <span>· fired {alert.fireCount}×{alert.lastFiredAt ? ` · last ${rel(alert.lastFiredAt)}` : ''}</span>}
        </div>
      </div>
      {canManage && (
        <button onClick={onToggle} style={iconBtn} title={alert.status === 'paused' ? 'Resume' : 'Pause'} aria-label={alert.status === 'paused' ? 'Resume' : 'Pause'}>
          {alert.status === 'paused' ? '▶' : '⏸'}
        </button>
      )}
    </div>
  );
}

function ruleText(a) {
  const metric = (a.source === 'metric' ? a.metricLabel : a.tileName) || 'the number';
  const t = fmtNum(a.threshold, a.unit);
  if (a.ruleType === 'stock_pct') return `When any ${/categor/.test(a.groupBy || '') ? 'ticket category' : 'ticket type'} has ${a.threshold}% or fewer left`;
  if (a.ruleType === 'sold_out') return `When ${metric} sells out`;
  if (a.ruleType === 'depletion') return `When ${metric} drops below ${t}`;
  return `When ${metric} ${(a.operator === 'lte' || a.operator === 'lt') ? 'drops to' : 'reaches'} ${t}`;
}
function fmtNum(v, unit) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString('en-ZA') : String(n);
  if (unit === 'ZAR') return `R${s}`;
  if (unit === '%') return `${s}%`;
  return unit && unit !== 'count' ? `${s} ${unit}` : s;
}
function rel(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function Skel({ w = '100%', h = 14, r = 8, style }) { return <div className="skel" style={{ width: w, height: h, borderRadius: r, ...style }} />; }


// Reusable alert templates — this client's own plus 🌐 global ones. Tap to start a
// new alert from one; delete removes it (global ones admin-only, enforced server-side).
function TemplatesView({ templates, canUse, onUse, onDelete }) {
  if (templates === null) return <Skel w="100%" h={60} r={12} />;
  if (!templates.length) {
    return (
      <div style={empty}>
        No templates yet. Build an alert, then tap <b>📑 Template</b> in its editor to save the setup here and reuse it.
      </div>
    );
  }
  const desc = (p = {}) => {
    const bits = [];
    if (p.source === 'metric' && p.metricRef?.metricLabel) bits.push(p.metricRef.metricLabel);
    else if (p.source === 'tile' && p.tileRef?.tileName) bits.push(p.tileRef.tileName);
    if (p.threshold != null && p.ruleType !== 'sold_out') bits.push(`${p.ruleType === 'depletion' ? 'below ' : ''}${p.threshold}${p.unit && p.unit !== 'count' ? ` ${p.unit}` : ''}`);
    if (p.ruleType === 'sold_out') bits.push('sold out');
    return bits.join(' · ');
  };
  // Howler's seeded starter pack leads; hand-made templates follow. A starter's
  // emoji + blurb ride in payload.meta (see server/starterTemplates.js).
  const isStarter = (t) => String(t.id || '').startsWith('seed-');
  const ordered = [...templates].sort((a, b) => (isStarter(b) ? 1 : 0) - (isStarter(a) ? 1 : 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {ordered.map((t) => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '12px 14px' }}>
          {t.payload?.meta?.emoji && <span style={{ fontSize: 21, flexShrink: 0 }}>{t.payload.meta.emoji}</span>}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              {isStarter(t)
                ? <span title="Howler starter template — the metric is pre-picked, just set your number" style={{ fontSize: 10, fontWeight: 800, color: 'var(--brand)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>STARTER</span>
                : t.global && <span title="Global template (available to every client)" style={{ fontSize: 10, fontWeight: 800, color: 'var(--brand)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>🌐 GLOBAL</span>}
              <span style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.payload?.name || t.name}</span>
            </span>
            {(t.payload?.meta?.blurb || desc(t.payload)) && <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.45 }}>{t.payload?.meta?.blurb || desc(t.payload)}</span>}
          </span>
          {canUse && <button onClick={() => onUse(t)} style={addBtn}>＋ Use</button>}
          <button onClick={() => onDelete(t)} aria-label="Delete template" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 13, padding: 4 }}>🗑</button>
        </div>
      ))}
    </div>
  );
}

const eventName = { fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' };
const tagHead = { fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--hairline)', borderRadius: 980, padding: '3px 11px' };
const addBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--brand)', borderRadius: 9, fontSize: 12.5, fontWeight: 700, padding: '6px 11px', cursor: 'pointer', flexShrink: 0 };
const card = { display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '12px 14px' };
const iconBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 9, width: 38, height: 34, fontSize: 13, cursor: 'pointer', flexShrink: 0 };
const empty = { padding: '28px 18px', textAlign: 'center', color: 'var(--muted)', border: '1px dashed var(--hairline)', borderRadius: 14 };
